import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Gift, Sparkles, ArrowRight, Receipt, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

// Use axios directly to avoid auth interceptors causing redirects
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Founder launch date
const FOUNDER_LAUNCH_DATE = new Date('2026-07-31T09:00:00+02:00');

// Countdown hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
};

const PLAN_NAMES = {
  creador: 'Plan Creador',
  agencia: 'Plan Agencia'
};

const PLAN_PRICES = {
  creador: { monthly: 39, yearly: 390 },
  agencia: { monthly: 119, yearly: 1190 }
};

// Meta Pixel Purchase Event
const trackMetaPurchase = (plan, interval, isFounders, value) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Purchase', {
      value: value,
      currency: 'EUR',
      content_name: `${PLAN_NAMES[plan] || plan} - ${interval === 'yearly' ? 'Anual' : 'Mensual'}`,
      content_type: 'subscription',
      content_ids: [`${plan}_${interval}`],
      contents: [{
        id: `${plan}_${interval}`,
        quantity: 1,
        item_price: value
      }],
      predicted_ltv: interval === 'yearly' ? value : value * 12,
      num_items: 1,
      status: 'subscribed',
      subscription_type: isFounders ? 'founders' : 'normal'
    });
    console.log('Meta Pixel Purchase event tracked:', { plan, interval, value });
  }
};

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollingCountRef = useRef(0);
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('No se encontró la sesión de pago');
      setLoading(false);
      return;
    }

    // Prevent multiple polling chains
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    const checkStatus = async () => {
      try {
        // Use axios directly (not api instance) to avoid auth interceptors
        const response = await axios.get(`${API_URL}/api/billing/stripe/checkout/status/${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === 'paid') {
          setStatus(data);
          setLoading(false);
        } else if (data.status === 'expired') {
          setError('La sesión de pago ha expirado');
          setLoading(false);
        } else if (pollingCountRef.current < 10) {
          // Keep polling
          pollingCountRef.current += 1;
          setTimeout(checkStatus, 2000);
        } else {
          setError('Tiempo de espera agotado. Si el pago se realizó, contacta con soporte.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking status:', err);
        if (pollingCountRef.current < 5) {
          pollingCountRef.current += 1;
          setTimeout(checkStatus, 2000);
        } else {
          setError('Error al verificar el pago. Si el pago se realizó correctamente, recibirás un email de confirmación.');
          setLoading(false);
        }
      }
    };

    checkStatus();
    
    return () => {
      isPollingRef.current = false;
    };
  }, [sessionId]);

  // Redirect to register if user needs to create account
  useEffect(() => {
    if (status && !status.user_id && status.customer_email) {
      navigate(`/register?session_id=${sessionId}&email=${encodeURIComponent(status.customer_email)}`);
    }
  }, [status, sessionId, navigate]);

  // Track Meta Pixel Purchase event when payment is confirmed
  useEffect(() => {
    if (status && status.payment_status === 'paid') {
      const plan = status.plan || 'creador';
      const interval = status.interval || 'monthly';
      const isFounders = status.is_founders === 'true' || status.is_founders === true;
      const price = PLAN_PRICES[plan]?.[interval] || (interval === 'yearly' ? 390 : 39);
      
      trackMetaPurchase(plan, interval, isFounders, price);
    }
  }, [status]);

  // Countdown for Founders - MUST be called before any early returns
  const timeLeft = useCountdown(FOUNDER_LAUNCH_DATE);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0B14] to-[#12131E] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#00E5A0] animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-2">Verificando tu pago...</h2>
          <p className="text-white/60">Esto solo tomará unos segundos</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0B14] to-[#12131E] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Error en el pago</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-[#00E5A0] hover:bg-[#00E5A0]/90 text-black font-semibold"
            >
              Volver al inicio
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = 'mailto:soporte@fascinai.pro'}
              className="w-full border-white/20 text-white hover:bg-white/10"
            >
              Contactar soporte
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const planName = PLAN_NAMES[status?.plan] || status?.plan;
  const isFounders = status?.is_founders;
  const accessStartsAt = status?.access_starts_at; // "2026-07-31" for founders pre-sale

  // For Founders: don't show "Ir a Dashboard" - they can't access yet
  const showDashboardButton = !accessStartsAt;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0B14] to-[#12131E] flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center mb-6">
          {/* Success Icon */}
          <div className="relative mb-8">
            <div className="w-20 h-20 bg-[#00E5A0]/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-[#00E5A0]" />
            </div>
            {isFounders && (
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                FOUNDER
              </div>
            )}
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">
            {accessStartsAt ? '🎉 ¡Ya eres Founder de FASCINAI!' : '¡Bienvenido a FASCINAI ONE!'}
          </h1>
          <p className="text-white/60 text-lg mb-6">
            {accessStartsAt 
              ? 'Tu lugar está asegurado al precio de fundador'
              : 'Tu suscripción está activa'
            }
          </p>

          {/* COUNTDOWN for Founders */}
          {accessStartsAt && (
            <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl p-5 mb-6">
              <p className="text-amber-300 text-sm mb-3 flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                Tu acceso se activa en:
              </p>
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-black/30 rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">{timeLeft.days}</div>
                  <div className="text-xs text-white/60">días</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">{timeLeft.hours}</div>
                  <div className="text-xs text-white/60">horas</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">{timeLeft.minutes}</div>
                  <div className="text-xs text-white/60">min</div>
                </div>
                <div className="bg-black/30 rounded-lg p-3">
                  <div className="text-2xl font-bold text-white">{timeLeft.seconds}</div>
                  <div className="text-xs text-white/60">seg</div>
                </div>
              </div>
              <p className="text-amber-200/70 text-xs mt-3">31 de julio de 2026 • 09:00 (Madrid)</p>
            </div>
          )}

          {/* Plan Details */}
          <div className="bg-white/5 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60">Plan</span>
              <span className="text-white font-semibold">{planName}</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60">Facturación</span>
              <span className="text-white font-semibold">
                {status?.interval === 'yearly' ? 'Anual' : 'Mensual'}
              </span>
            </div>
            
            {/* 🎯 FOUNDER PRE-SALE INFO */}
            {accessStartsAt && (
              <>
                <div className="border-t border-white/10 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/60">Tu acceso se activa</span>
                    <span className="text-emerald-400 font-semibold">31 de julio de 2026</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Primer cobro</span>
                    <span className="text-emerald-400 font-semibold">31 de julio de 2026</span>
                  </div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mt-4">
                  <p className="text-emerald-300 text-sm">
                    <strong>No se te ha cobrado hoy.</strong> Tu tarjeta se guardó de forma segura y el primer cargo será el 31 de julio.
                  </p>
                </div>
              </>
            )}
            
            {isFounders && !accessStartsAt && (
              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <Gift className="w-5 h-5" />
                  <span className="font-semibold">Bonus de Founder</span>
                </div>
                <p className="text-white/60 text-sm">
                  Has desbloqueado créditos extra por ser de los primeros en unirte.
                </p>
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3 mt-6">
            {/* For Founders: show "Volver al inicio" as they can't access dashboard yet */}
            {accessStartsAt ? (
              <>
                <Button 
                  onClick={() => navigate('/')}
                  className="w-full bg-[#00E5A0] hover:bg-[#00E5A0]/90 text-black font-semibold h-12"
                >
                  Volver al inicio
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-[#00E5A0] hover:bg-[#00E5A0]/90 text-black font-semibold h-12"
                >
                  Ir a mi Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const tokenKey = 'la_tk';
                      const encoded = localStorage.getItem(tokenKey);
                      let token = null;
                      if (encoded) {
                        try {
                          token = decodeURIComponent(atob(encoded).split('').reverse().join(''));
                        } catch {
                          token = encoded;
                        }
                      }
                      
                      const res = await axios.post(`${API_URL}/api/billing/stripe/create-portal-session`, {}, {
                        headers: { 
                          'origin': window.location.origin,
                          'Content-Type': 'application/json',
                          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                        }
                      });
                      if (res.data.url) {
                        window.location.href = res.data.url;
                      }
                    } catch (err) {
                      console.error('Error opening portal:', err);
                    }
                  }}
                  className="w-full border-white/20 text-white hover:bg-white/10 h-12"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Ver mi factura
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Help Text */}
        <p className="text-center text-white/40 text-sm">
          Recibirás un email de confirmación en {status?.customer_email}.
          <br />
          ¿Necesitas ayuda? <Link to="/soporte" className="text-[#00E5A0] hover:underline">Contacta con soporte</Link>
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
